// SheetRenameModal.tsx — give a Google Sheet a readable name.
// The name is only a label: it's applied to every sync of the sheet and its
// beacon, while leads keep pointing at the spreadsheet ID, so filters never break.

import React, { useEffect, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Check, Loader2, Pencil, Tag, X } from 'lucide-react';
import { GlobalButton } from '../../../components/GlobalComponents/GlobalButton';
import { G, C } from '../../../components/GlobalComponents/uiTokens';
import { Banner, TONES, shortId } from './SheetImportDetailsModal';
import { renameSheet } from './leadOrigin';

const NAME_MAX = 80;

interface SheetRenameModalProps {
  visible: boolean;
  sheetId: string | null;
  currentName?: string;
  onHide: () => void;
  onRenamed: (sheetId: string, name: string) => void;
}

const SheetRenameModal: React.FC<SheetRenameModalProps> = ({ visible, sheetId, currentName, onHide, onRenamed }) => {
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (visible) {
      setName(currentName ?? '');
      setError(null);
    }
  }, [visible, currentName]);

  const handleHide = () => {
    if (!saving) onHide();
  };

  const save = async () => {
    if (!sheetId) return;
    setSaving(true);
    setError(null);
    try {
      const trimmed = name.trim().replace(/\s+/g, ' ');
      await renameSheet(sheetId, trimmed);
      onRenamed(sheetId, trimmed);
      onHide();
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Could not rename the sheet.');
    } finally {
      setSaving(false);
    }
  };

  const header = () => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '13px' }}>
      <div
        style={{
          width: 44,
          height: 44,
          borderRadius: '12px',
          flexShrink: 0,
          background: `linear-gradient(135deg, ${C.teal}, ${C.tealMid})`,
          color: C.lime,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 6px 18px rgba(17,85,80,0.25)',
        }}
      >
        <Pencil size={19} />
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: '1.01rem', fontWeight: 700, color: '#111827', lineHeight: 1.2, marginBottom: '3px' }}>Name this sheet</div>
        <div style={{ fontSize: '0.71rem', color: C.sub, fontWeight: 500 }}>
          Sheet <strong style={{ color: C.teal, fontFamily: 'monospace' }}>{sheetId ? shortId(sheetId) : ''}</strong>
        </div>
      </div>
    </div>
  );

  return (
    <Dialog
      visible={visible}
      onHide={handleHide}
      header={header}
      className="srm-dialog"
      maskClassName="srm-mask"
      style={{ width: '90vw', maxWidth: '440px' }}
      modal
      closable={!saving}
      dismissableMask={!saving}
      draggable={false}
      resizable={false}
      blockScroll
    >
      <style>{`
        .srm-mask { backdrop-filter: blur(3px); background-color: rgba(17,40,38,0.28) !important; }
        .srm-dialog.p-dialog { background: ${G.shellBg} !important; border: 1px solid ${G.shellBorder} !important; border-radius: 18px !important; padding: 5px !important; overflow: hidden; box-shadow: 0 24px 48px -12px rgba(17,85,80,0.25) !important; }
        .srm-dialog .p-dialog-header { background: white !important; border: 1px solid ${G.line}; border-bottom-color: ${G.soft}; border-radius: 13px 13px 0 0 !important; padding: 16px 18px !important; }
        .srm-dialog .p-dialog-content { background: white !important; border: 1px solid ${G.line}; border-top: none; border-radius: 0 0 13px 13px !important; padding: 12px !important; }
        .srm-spin { animation: srmSpin 0.9s linear infinite; }
        @keyframes srmSpin { to { transform: rotate(360deg); } }
      `}</style>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '11px' }}>
        <div style={{ background: G.shellBg, border: `1px solid ${G.shellBorder}`, borderRadius: '13px', padding: '4.6px' }}>
          <div style={{ background: C.white, border: `1px solid ${G.line}`, borderRadius: '9.2px', padding: '14px 16px' }}>
            <label
              htmlFor="srm-name"
              style={{ display: 'block', fontSize: '0.68rem', fontWeight: 700, color: C.teal, textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '5.5px' }}
            >
              Display name
            </label>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                height: '40px',
                padding: '0 11px',
                border: `1px solid ${G.line}`,
                borderRadius: '8.3px',
                background: saving ? '#f9fafb' : C.white,
              }}
            >
              <Tag size={14} color={C.muted} />
              <input
                id="srm-name"
                autoFocus
                value={name}
                maxLength={NAME_MAX}
                disabled={saving}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && !saving && save()}
                placeholder="e.g. Diwali Meta Leads"
                style={{ flex: 1, minWidth: 0, height: '100%', border: 'none', outline: 'none', background: 'transparent', fontSize: '0.79rem', color: '#111827' }}
                autoComplete="off"
              />
              <span style={{ fontSize: '0.6rem', color: C.muted }}>
                {name.length}/{NAME_MAX}
              </span>
            </div>
            <div style={{ fontSize: '0.64rem', color: C.muted, marginTop: '6px', lineHeight: 1.5 }}>
              Used on every sync of this sheet, its beacon and the Leads filter. Leave empty to show the sheet ID again.
            </div>
          </div>
        </div>

        {error && (
          <Banner tone={TONES.red} icon={<X size={15} />}>
            {error}
          </Banner>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '7.4px' }}>
          <GlobalButton label="Cancel" variant="secondary" icon={X} onClick={handleHide} disabled={saving} />
          <button
            type="button"
            onClick={save}
            disabled={saving}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '0 18px',
              height: '36px',
              borderRadius: '8px',
              border: 'none',
              background: `linear-gradient(135deg, ${C.teal}, ${C.tealMid})`,
              color: '#fff',
              fontSize: '0.72rem',
              fontWeight: 800,
              cursor: saving ? 'wait' : 'pointer',
              opacity: saving ? 0.85 : 1,
            }}
          >
            {saving ? <Loader2 size={14} className="srm-spin" /> : <Check size={14} strokeWidth={3} />}
            {saving ? 'SAVING…' : 'SAVE NAME'}
          </button>
        </div>
      </div>
    </Dialog>
  );
};

export default SheetRenameModal;
